import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const OnboardingContext = createContext();

const ONBOARDING_STEPS = {
  guest: [
    'project-selection',
    'editor-loaded',
    'ai-interaction',
    'show-mode-selected',
    'submit-request',
    'processing-request',
    'recommendations-ready',
    'editing-tools',
    'save-limitations'
  ],
  member: [
    'project-selection',
    'editor-loaded',
    'ai-interaction',
    'show-mode-selected',
    'submit-request',
    'processing-request',
    'recommendations-ready',
    'editing-tools',
    'ready-to-design'
  ],
  memberNewProject: [
    'create-project',
    'enter-project-name',
    'select-facade-image',
    'upload-project',
    'editor-loaded',
    'ai-interaction',
    'show-mode-selected',
    'submit-request',
    'processing-request',
    'recommendations-ready',
    'editing-tools',
    'ready-to-design'
  ],
  editorTour: [
    'editor-tour-welcome',
    'editor-tour-save',
    'editor-tour-tools',
    'editor-tour-zoom',
    'editor-tour-video',
    'editor-tour-designer'
  ],
  signup: [
    'purchase-plan'
  ]
};

const getCompletionStorageKey = (flow) => `facade_onboarding_${flow || 'guest'}_completed`;

const markFlowCompleted = (flow) => {
  const storageKey = getCompletionStorageKey(flow);
  localStorage.setItem(storageKey, 'true');

  if (flow === 'guest') {
    localStorage.setItem('guestOnboardingCompleted', 'true');
  }
};

const clearFlowCompletion = (flow) => {
  const storageKey = getCompletionStorageKey(flow);
  localStorage.removeItem(storageKey);

  if (flow === 'guest') {
    localStorage.removeItem('guestOnboardingCompleted');
  }
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [currentStep, setCurrentStep] = useState('project-selection');
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [appState, setAppState] = useState({
    projectName: '',
    projectId: null,
    hasActiveProjectLoaded: false,
    editorVisible: false,
    accountModalOpen: false,
    aiDesignerVisible: false,
    aiShowModeSelected: false,
    aiLoading: false
  });

  const steps = useMemo(() => {
    if (!currentFlow) {
      return ONBOARDING_STEPS.guest;
    }

    return ONBOARDING_STEPS[currentFlow] || ONBOARDING_STEPS.guest;
  }, [currentFlow]);

  const launchOnboarding = useCallback((flow = 'guest', startStep = null) => {
    const normalizedFlow = ONBOARDING_STEPS[flow] ? flow : 'guest';
    const flowSteps = ONBOARDING_STEPS[normalizedFlow];
    const initialStep = flowSteps.includes(startStep) ? startStep : flowSteps[0];

    setCurrentFlow(normalizedFlow);
    setIsOnboardingActive(true);
    setCurrentStep(initialStep);

    const initialCompleted = new Set();
    const startIndex = flowSteps.indexOf(initialStep);
    if (startIndex > 0) {
      flowSteps.slice(0, startIndex).forEach((stepId) => initialCompleted.add(stepId));
    }
    setCompletedSteps(initialCompleted);
    return true;
  }, []);

  const startOnboarding = useCallback((flow = 'guest', startStep = null) => {
    const normalizedFlow = ONBOARDING_STEPS[flow] ? flow : 'guest';
    const storageKey = getCompletionStorageKey(normalizedFlow);
    const alreadyCompleted = localStorage.getItem(storageKey) || (
      normalizedFlow === 'guest' ? localStorage.getItem('guestOnboardingCompleted') : null
    );

    if (alreadyCompleted) {
      setIsOnboardingActive(false);
      setCurrentFlow(normalizedFlow);
      return false;
    }

    return launchOnboarding(normalizedFlow, startStep);
  }, [launchOnboarding]);

  const restartOnboarding = useCallback((flow = 'guest', startStep = null) => {
    const normalizedFlow = ONBOARDING_STEPS[flow] ? flow : 'guest';
    clearFlowCompletion(normalizedFlow);
    return launchOnboarding(normalizedFlow, startStep);
  }, [launchOnboarding]);

  const completeStep = useCallback((stepId) => {
    setCompletedSteps((prevCompleted) => {
      const nextCompleted = new Set(prevCompleted);
      nextCompleted.add(stepId);
      return nextCompleted;
    });

    const activeSteps = currentFlow ? (ONBOARDING_STEPS[currentFlow] || ONBOARDING_STEPS.guest) : ONBOARDING_STEPS.guest;
    const currentIndex = activeSteps.indexOf(stepId);

    if (currentIndex < activeSteps.length - 1) {
      setCurrentStep(activeSteps[currentIndex + 1]);
      return;
    }

    setIsOnboardingActive(false);
    if (currentFlow) {
      markFlowCompleted(currentFlow);
    }
  }, [currentFlow]);

  const skipOnboarding = useCallback(() => {
    setIsOnboardingActive(false);
    if (currentFlow) {
      markFlowCompleted(currentFlow);
    }
  }, [currentFlow]);

  const stopOnboarding = useCallback(() => {
    setIsOnboardingActive(false);
    setCurrentFlow(null);
    setCompletedSteps(new Set());
  }, []);

  const updateAppState = useCallback((updates) => {
    setAppState(prev => ({ ...prev, ...updates }));
  }, []);

  const isStepActive = useCallback((stepId) => {
    return isOnboardingActive && currentStep === stepId;
  }, [isOnboardingActive, currentStep]);

  const isStepCompleted = useCallback((stepId) => {
    return completedSteps.has(stepId);
  }, [completedSteps]);

  useEffect(() => {
    setIsOnboardingActive(false);
  }, []);

  const value = {
    isOnboardingActive,
    currentFlow,
    currentStep,
    completedSteps,
    appState,
    startOnboarding,
    restartOnboarding,
    completeStep,
    skipOnboarding,
    stopOnboarding,
    updateAppState,
    isStepActive,
    isStepCompleted,
    steps
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
